/* eslint-disable class-methods-use-this, react/no-unused-prop-types */
/*
 * Usage:
 * <BrzwCalendar
 *   activeDate={activeDate}
 *   data={data}
 * />
 *
 * activeDate - calendar active date wrapped in moment
 *
 * data - object with sorted by month day tasks and reservations
 * {
 *   '2018-05-25': {
 *     reservations: [{reservation_object}],
 *     tasks: [{task_object}],
 *   },
 *   '2018-05-26': {
 *     reservations: [{reservation_object}],
 *     tasks: [{task_object}],
 *   },
 *   '2018-05-27': {
 *     reservations: [{reservation_object}],
 *     tasks: [{task_object}],
 *   },
 * }
 */
import React, { Component, Fragment } from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import _ from 'lodash';

import {
  Cell,
  CellModalPopup,
  Container as CalendarContainer,
  Heading,
  MonthDay,
  Reservation,
  Row,
  Rows as CalendarRows,
  ServiceSummary,
  ServiceSummarySkeleton,
  SingleTask,
} from './commons';
import {
  CALENDAR_CELL_TYPES,
  CALENDAR_COLUMNS_NUMBER,
  CALENDAR_THEMES,
  CALENDAR_SOURCES,
  SCHEDULE_CELL_TYPE,
} from './constants';
import * as calendarHelpers from './helpers';

import styles from '../../css/components/_brzw-calendar.scss';
import './brzw-calendar-theme-third.scss';

const sharedVariables = {
  calendarMinHeight: _.parseInt(styles.homeCalendarMinHeight),
  calendarMinWidth: _.parseInt(styles.homeCalendarMinWidth),
  calendarMoreButtonHeight: _.parseInt(styles.homeCalendarMoreButtonHeight),
  calendarMoreButtonTopMargin: _.parseInt(styles.homeCalendarMoreButtonTopMargin),
  calendarMoreButtonBottomMargin: _.parseInt(styles.homeCalendarMoreButtonBottomMargin),
  calendarReservationBottomMargin: _.parseInt(styles.homeCalendarReservationBottomMargin),
  calendarReservationHeight: _.parseInt(styles.homeCalendarReservationHeight),
  calendarTaskTopMargin: _.parseInt(styles.homeCalendarTaskTopMargin),
  calendarTaskTopHeight: _.parseInt(styles.homeCalendarTaskTopHeight),
  calendarTitleHeight: _.parseInt(styles.homeCalendarTitleHeight),
};

class BrzwCalendar extends Component {
  constructor(props) {
    super(props);

    this.weekdays =
      props.theme === CALENDAR_THEMES.THIRD ? moment.weekdaysShort() : moment.weekdays();
    this.numberOfRows = calendarHelpers.calculateRowsNumber(props.activeDate);
    this.sizeOfWeekdays = _.size(this.weekdays);
    this.rowRef = React.createRef();

    this.state = {
      hoveredReservation: 0,
      firstReservations: {},
      fullCell: {},
    };

    this.GUARD_RESET = false;
    this.EXTRA_BUTTON =
      sharedVariables.calendarMoreButtonHeight +
      sharedVariables.calendarMoreButtonTopMargin +
      sharedVariables.calendarMoreButtonBottomMargin;
    this.RESERVATION_HEIGHT =
      sharedVariables.calendarReservationBottomMargin + sharedVariables.calendarReservationHeight;
    this.SPACE_AFTER_TASK = sharedVariables.calendarTaskTopMargin;
    this.TASK_HEIGHT = sharedVariables.calendarTaskTopHeight;
    this.TITLE_HEIGHT = sharedVariables.calendarTitleHeight;

    this.onCellClick = this.onCellClick.bind(this);
    this.renderCell = this.renderCell.bind(this);
    this.renderCellContent = this.renderCellContent.bind(this);
    this.renderCellHeaderContent = this.renderCellHeaderContent.bind(this);
    this.renderColumns = this.renderColumns.bind(this);
    this.renderFullCell = this.renderFullCell.bind(this);
    this.renderHeadings = this.renderHeadings.bind(this);
    this.renderRow = this.renderRow.bind(this);
    this.renderRows = this.renderRows.bind(this);
    this.renderSummaryContent = this.renderSummaryContent.bind(this);
    this.renderTasks = this.renderTasks.bind(this);
    this.setEmptyFirstReservations = this.setEmptyFirstReservations.bind(this);
    this.setFirstIdOfReservation = this.setFirstIdOfReservation.bind(this);
    this.setHoveredReservation = this.setHoveredReservation.bind(this);
    this.toggleFullCellContent = this.toggleFullCellContent.bind(this);
  }

  componentWillUpdate(nextProps) {
    const { activeDate: nextActiveDate } = nextProps;
    const { activeDate } = this.props;

    // Recalculate rows number when active date has changed
    if (!activeDate.isSame(nextActiveDate, 'days')) {
      this.numberOfRows = calendarHelpers.calculateRowsNumber(nextActiveDate);
      this.setEmptyFirstReservations();
    }
  }

  onCellClick(cellDate) {
    const { onCellClick } = this.props;

    return () => {
      onCellClick(cellDate);
    };
  }

  setEmptyFirstReservations() {
    this.GUARD_RESET = true;
    this.setState({ firstReservations: {} }, () => {
      this.GUARD_RESET = false;
    });
  }

  setHoveredReservation(id) {
    this.setState(prevState => {
      if (id !== prevState.hoveredReservation) {
        return { hoveredReservation: id };
      }
      return {};
    });
  }

  setFirstIdOfReservation(id, date) {
    if (!this.GUARD_RESET) {
      this.setState(prevState => {
        if (!_.includes(_.keys(prevState.firstReservations), _.toString(id))) {
          return {
            firstReservations: { ...prevState.firstReservations, [id]: date },
          };
        }

        // prevent rails condition
        if (this.GUARD_RESET) {
          return { firstReservations: {} };
        }

        return { firstReservations: prevState.firstReservations };
      });
    }
  }

  toggleFullCellContent(tasks, row, column, cellDate) {
    return () => {
      if (_.isEmpty(tasks)) this.setState({ fullCell: {} });
      else {
        this.setState({ fullCell: { tasks, row, column, cellDate } });
      }
    };
  }

  renderHeadings() {
    const { theme } = this.props;

    const mappedHeadings = _.map(this.weekdays, (day, index) => (
      <Cell
        key={`calendar-heading-cell-${day}-${index}`}
        theme={theme}
        centered
        disableTrigger
        heading
      >
        <Heading primary={day} theme={theme} />
      </Cell>
    ));

    return (
      <Row
        expandCellProps={{
          hidden: true,
        }}
        rowType={SCHEDULE_CELL_TYPE.HEADING}
      >
        {mappedHeadings}
      </Row>
    );
  }

  renderRows() {
    return calendarHelpers.mapElements(this.numberOfRows, index => this.renderRow(index));
  }

  renderRow(rowIndex) {
    return (
      <Row
        key={`calendar-row-${rowIndex}`}
        expandCellProps={{
          hidden: true,
        }}
        firstRow={rowIndex === 0}
        lastRow={rowIndex === this.numberOfRows - 1}
        rowRef={this.rowRef}
      >
        {this.renderColumns(rowIndex)}
      </Row>
    );
  }

  renderColumns(rowIndex) {
    return calendarHelpers.mapElements(CALENDAR_COLUMNS_NUMBER, index =>
      this.renderCell(rowIndex, index, true),
    );
  }

  renderCell(rowIndex, columnIndex, withHidingTasks) {
    const { activeDate, data, source, theme } = this.props;

    const cloneCellDate = calendarHelpers.cloneDate(activeDate);
    cloneCellDate.add(1, 'days');

    const thirdTheme = theme === CALENDAR_THEMES.THIRD;
    const cellType = calendarHelpers.getCellType(activeDate, rowIndex, columnIndex, thirdTheme);

    if (cellType === CALENDAR_CELL_TYPES.EMPTY) {
      return (
        <Cell
          headerContent={<Reservation empty />}
          key={`calendar-column-${rowIndex}-${columnIndex}`}
          theme={theme}
          disableTrigger
          isEmpty
        />
      );
    }

    const numberOfRows = this.numberOfRows - 1;
    const numberOfColumns = this.sizeOfWeekdays - 1;

    const { nextDayRow, nextDayColumn } = calendarHelpers.getCellPositionConfiguration(
      numberOfRows,
      numberOfColumns,
      columnIndex,
      rowIndex,
    );

    const cellPosition = calendarHelpers.calculateCellPosition(activeDate, rowIndex, columnIndex);
    const cellNextDayPosition = calendarHelpers.calculateCellPosition(
      activeDate,
      nextDayRow,
      nextDayColumn,
    );
    const cellDate = calendarHelpers.getCellDate(activeDate, cellPosition);
    const cellDateNextDay = calendarHelpers.getCellDate(activeDate, cellNextDayPosition);
    const isTodaysCell = calendarHelpers.isTodayDate(cellDate);
    const isBeforeTodayCell = calendarHelpers.isDayBeforeTodayDate(cellDate);
    const isAfterTodayCell = calendarHelpers.isDayAfterTodayDate(cellDate);
    const dataDateKey = calendarHelpers.getDataDateKey(cellDate);
    const dataDateKeyNextDay = calendarHelpers.getDataDateKey(cellDateNextDay);
    const cellData = _.get(data, dataDateKey, {});
    const cellDataNextDay = _.get(data, dataDateKeyNextDay, {});
    const firstDayOnCalendar = rowIndex === 0 && columnIndex === 0;

    const sortedReservations = _.chain(cellData.reservations)
      .sortBy('checkin_time')
      .sortBy('checkin_date')
      .value();
    const sortedCellData = { ...cellData, reservations: sortedReservations };
    const isScheduleSource = source === CALENDAR_SOURCES.SCHEDULE;

    const headerContent = this.renderCellHeaderContent(
      cellDate,
      sortedCellData.reservations,
      cellPosition,
      firstDayOnCalendar,
      columnIndex,
      cellDataNextDay.reservations,
    );

    return (
      <Cell
        activeCell={isTodaysCell}
        clickable={isScheduleSource}
        headerContent={headerContent}
        key={`calendar-column-${rowIndex}-${columnIndex}`}
        onCellClick={this.onCellClick(cellDate)}
        theme={theme}
        type={SCHEDULE_CELL_TYPE.DATA}
        disableTrigger
        noTaskLimit
        isBeforeToday={isBeforeTodayCell}
        isAfterTodayCell={isAfterTodayCell}
      >
        {isScheduleSource
          ? this.renderSummaryContent(cellData.tasks, cellData.reservations)
          : this.renderCellContent(
              cellData.tasks,
              withHidingTasks,
              rowIndex,
              columnIndex,
              cellDate,
            )}
      </Cell>
    );
  }

  renderCellHeaderContent(
    cellDate,
    reservations,
    cellPosition,
    firstDayOnCalendar,
    columnIndex,
    reservationPropsNextDay,
  ) {
    const { source, theme, activeDate, flyoutInformations, reservationTextDuration } = this.props;
    const { hoveredReservation, firstReservations } = this.state;

    let reservationProps = {
      empty: true,
      firstDayOnCalendar,
      theme,
    };

    if (!_.isEmpty(reservations) && source !== CALENDAR_SOURCES.SCHEDULE) {
      const reservationPropsByDate = calendarHelpers.getReservationProps(cellDate, reservations);

      if (CALENDAR_THEMES.THIRD === theme) {
        const cloneCellDate = _.cloneDeep(cellDate);
        cloneCellDate.add(1, 'days');

        const rowRef = _.invoke(this.rowRef, 'current.getBoundingClientRect');
        const rowWidth = _.get(rowRef, 'width');
        const cellWidth = rowWidth / _.size(this.weekdays);

        const reservationPropsByDateNextDay = calendarHelpers.getReservationProps(
          cloneCellDate,
          reservationPropsNextDay,
        );

        reservationProps = {
          ...reservationPropsByDate,
          cellDate,
          cellPosition,
          cellWidth,
          firstReservations,
          flyoutInformations,
          hoveredReservation,
          firstDayOnCalendar,
          reservationByDate: true,
          reservationPropsNextDay: reservationPropsByDateNextDay,
          setFirstIdOfReservation: this.setFirstIdOfReservation,
          setHoveredReservation: this.setHoveredReservation,
          reservationTextDuration,
          theme,
        };
      } else {
        reservationProps = {
          ...reservationPropsByDate,
          firstDayOnCalendar,
          theme,
        };
      }
    }

    if (CALENDAR_THEMES.THIRD === theme) {
      const isFirstDayOfMonth = cellPosition === 1;
      let month = isFirstDayOfMonth ? moment(activeDate).format('MMM') : null;
      const { day } = calendarHelpers.getCorrectDateByCellPosition(activeDate, cellPosition);
      const daysInMonth = activeDate.daysInMonth();
      const dayInOldOrNextMonth = cellPosition <= 0 || cellPosition > daysInMonth;

      if (dayInOldOrNextMonth) {
        month = null;
      }

      return (
        <Fragment>
          <MonthDay day={day} theme={theme} month={month} />
          <Reservation {...reservationProps} />
        </Fragment>
      );
    }

    return (
      <Fragment>
        <Reservation {...reservationProps} />
        <MonthDay day={cellPosition} theme={theme} />
      </Fragment>
    );
  }

  renderTasks(tasks, allTasks) {
    const { theme, flyoutInformations } = this.props;
    const thirdTheme = CALENDAR_THEMES.THIRD === theme;

    return _.map(tasks, (task, index) => {
      let singleTaskProps = calendarHelpers.getSingleTaskProps(task);
      if (thirdTheme) {
        singleTaskProps = _.omit(singleTaskProps, 'onTaskClick');
      }

      return (
        <SingleTask
          allTasks={allTasks}
          flyoutInformations={flyoutInformations}
          key={`calendar-cell-content-${index}-${task.id}`}
          withText={thirdTheme}
          {...singleTaskProps}
        />
      );
    });
  }

  renderCellContent(tasks, withHidingTasks, rowIndex, columnIndex, cellDate) {
    const { theme } = this.props;

    if (_.isEmpty(tasks)) {
      return null;
    }

    if (CALENDAR_THEMES.THIRD === theme && withHidingTasks) {
      const tasksHeight = countTasks =>
        countTasks > 1
          ? countTasks * (this.TASK_HEIGHT + this.SPACE_AFTER_TASK) - this.SPACE_AFTER_TASK
          : countTasks * this.TASK_HEIGHT;

      const heightRow = _.get(
        this.rowRef,
        'current.offsetHeight',
        sharedVariables.calendarMinHeight,
      );
      const availableHeight = heightRow - (this.TITLE_HEIGHT + this.RESERVATION_HEIGHT);
      const tasksHeightCount = tasksHeight(_.size(tasks));
      const tasksAreCover = availableHeight - tasksHeightCount;

      if (tasksAreCover >= 0) {
        return this.renderTasks(tasks, tasks);
      }

      const placeForTasksWithExpandButton = _.floor(
        (availableHeight - this.EXTRA_BUTTON) / (this.TASK_HEIGHT + this.SPACE_AFTER_TASK),
      );

      const tasksToRender = _.slice(tasks, 0, placeForTasksWithExpandButton);

      return (
        <Fragment>
          {this.renderTasks(tasksToRender, tasks)}

          <button
            onClick={this.toggleFullCellContent(tasks, rowIndex, columnIndex, cellDate)}
            className="brzw-calendar-tasks-more"
            type="button"
          >
            {_.size(tasks) - _.size(tasksToRender)} more
          </button>
          {this.renderFullCell(rowIndex, columnIndex)}
        </Fragment>
      );
    }

    return this.renderTasks(tasks, tasks);
  }

  renderFullCell(rowIndex, columnIndex) {
    const { fullCell } = this.state;
    const { flyoutInformations, isFlyoutFinishedAnimation } = this.props;
    const tasks = _.get(fullCell, 'tasks');
    const row = _.get(fullCell, 'row');
    const column = _.get(fullCell, 'column');
    const cellDate = _.get(fullCell, 'cellDate');
    const rowRef = _.get(this.rowRef, 'current');
    const isOpen = _.isArray(tasks) && rowIndex === row && columnIndex === column;

    if (isOpen) {
      const rowParams = _.invoke(rowRef, 'getBoundingClientRect');
      const rowWidth = _.get(rowParams, 'width');
      const height = _.get(rowParams, 'height');

      const width = rowWidth / _.size(this.weekdays);
      const bottom = _.floor((this.numberOfRows - 1 - row) * height);
      const right = rowWidth - _.floor(column * width + width);
      const lastRow = row === this.numberOfRows - 1;
      const content = !!tasks && this.renderCell(rowIndex, columnIndex, false);

      const cellSizes = {
        bottom,
        height,
        right,
        width,
      };

      const position = calendarHelpers.countPosition(
        _.size(this.weekdays),
        this.numberOfRows,
        row,
        column,
        cellSizes,
        !_.isEmpty(flyoutInformations) && isFlyoutFinishedAnimation,
      );

      return (
        <CellModalPopup
          cellDate={cellDate}
          cellSizes={cellSizes}
          columnIndex={column}
          content={content}
          isOpen={isOpen}
          lastRow={lastRow}
          position={position}
          rowIndex={row}
          toggle={this.toggleFullCellContent()}
        />
      );
    }

    return null;
  }

  renderSummaryContent(tasks, reservations) {
    const { isFetching } = this.props;

    if (isFetching) {
      return <ServiceSummarySkeleton />;
    } else if (!tasks && !reservations) {
      return null;
    }

    return (
      <ServiceSummary
        backToBacksCount={_.get(reservations, 'back_to_backs', 0)}
        checkinsCount={_.get(reservations, 'checkins', 0)}
        checkoutsCount={_.get(reservations, 'checkouts', 0)}
        cleaningsCount={_.get(tasks, 'housekeeping', 0)}
        inspectionsCount={_.get(tasks, 'inspection', 0)}
        maintenanceCount={_.get(tasks, 'maintenance', 0)}
      />
    );
  }

  render() {
    const { theme } = this.props;

    return (
      <CalendarContainer theme={theme}>
        <CalendarRows>
          {this.renderHeadings()}
          {this.renderRows()}
        </CalendarRows>
      </CalendarContainer>
    );
  }
}

BrzwCalendar.propTypes = {
  activeDate: PropTypes.shape({}),
  data: PropTypes.shape({}),
  flyoutInformations: PropTypes.shape({}),
  isFetching: PropTypes.bool,
  isFlyoutFinishedAnimation: PropTypes.bool,
  isProperty: PropTypes.bool,
  onCellClick: PropTypes.func,
  reservationTextDuration: PropTypes.shape({}),
  source: PropTypes.oneOf(_.values(CALENDAR_SOURCES)),
  theme: PropTypes.oneOf(_.values(CALENDAR_THEMES)),
};

BrzwCalendar.defaultProps = {
  activeDate: moment(),
  data: {},
  isFetching: false,
  onCellClick: () => {},
  reservationTextDuration: {},
  source: CALENDAR_SOURCES.PROPERTY,
  theme: CALENDAR_THEMES.PRIMARY,
};

export default BrzwCalendar;
